import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import {
  BASE_NODE_URL,
  TELNYX_SMS_GET_CATEGORIES,
  TELNYX_SMS_CREATE_CATEGORY,
  TELNYX_SMS_DELETE_CATEGORY,
  TELNYX_SMS_GET_CONTACTS,
  TELNYX_SMS_ADD_CONTACT,
  TELNYX_SMS_DELETE_CONTACT,
  TELNYX_SMS_IMPORT_CSV,
  TELNYX_SMS_SEND_MESSAGE,
  TELNYX_SMS_GET_CONVERSATIONS,
  TELNYX_SMS_GET_MESSAGES,
  TELNYX_SMS_REPLY_MESSAGE,
  TELNYX_SMS_UPDATE_CONTACT,
  TELNYX_SMS_UPDATE_CATEGORY,
  TELNYX_SMS_MARK_CONVERSATION_READ,
  TELNYX_SMS_GET_UNREAD_COUNT,
  TELNYX_SMS_GET_CAMPAIGNS,
  TELNYX_SMS_GET_CAMPAIGN_DETAILS,
} from "../../../Constants/Config";

const makeHeaders = (token) => ({
  headers: {
    "Content-Type": "multipart/form-data",
    Authorization: `Bearer ${token}`,
  },
});


// ─── Categories ───────────────────────────────────────────────

export const fetchCategories = createAsyncThunk(
  "telnyxMassSms/fetchCategories",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_GET_CATEGORIES,
        dataNew,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data.data || [];
      }
      return [];
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

export const createCategory = createAsyncThunk(
  "telnyxMassSms/createCategory",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_CREATE_CATEGORY,
        dataNew,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data;
      }
      return rejectWithValue({
        message: response?.data?.message || "Failed to create category",
        status: response?.status,
      });
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

export const updateCategory = createAsyncThunk(
  "telnyxMassSms/updateCategory",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_UPDATE_CATEGORY,
        dataNew,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data;
      }
      return rejectWithValue({
        message: response?.data?.message || "Failed to update category",
        status: response?.status,
      });
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

export const deleteCategory = createAsyncThunk(
  "telnyxMassSms/deleteCategory",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_DELETE_CATEGORY,
        dataNew,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data;
      }
      return rejectWithValue({
        message: response?.data?.message || "Failed to delete category",
        status: response?.status,
      });
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

// ─── Contacts ─────────────────────────────────────────────────

export const fetchContacts = createAsyncThunk(
  "telnyxMassSms/fetchContacts",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_GET_CONTACTS,
        dataNew,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data.data || [];
      }
      return [];
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

export const addContact = createAsyncThunk(
  "telnyxMassSms/addContact",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_ADD_CONTACT,
        dataNew,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data;
      }
      return rejectWithValue({
        message: response?.data?.message || "Failed to add contact",
        status: response?.status,
      });
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

export const updateContact = createAsyncThunk(
  "telnyxMassSms/updateContact",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_UPDATE_CONTACT,
        dataNew,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data;
      }
      return rejectWithValue({
        message: response?.data?.message || "Failed to update contact",
        status: response?.status,
      });
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

export const deleteContact = createAsyncThunk(
  "telnyxMassSms/deleteContact",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_DELETE_CONTACT,
        dataNew,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data;
      }
      return rejectWithValue({
        message: response?.data?.message || "Failed to delete contact",
        status: response?.status,
      });
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

export const importContactsCsv = createAsyncThunk(
  "telnyxMassSms/importContactsCsv",
  async (data, { rejectWithValue }) => {
    const { token, csv_file, category_id, ...authFields } = data;
    try {
      const formData = new FormData();
      // append auth fields (token_id, login_type, session_id)
      Object.entries(authFields).forEach(([key, val]) => {
        if (val !== undefined && val !== null) formData.append(key, val);
      });
      formData.append("csv_file", csv_file);
      if (category_id) formData.append("category_id", category_id);
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_IMPORT_CSV,
        formData,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data;
      }
      return rejectWithValue({
        message: response?.data?.message || "Failed to import CSV",
        status: response?.status,
      });
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

// ─── Messaging ────────────────────────────────────────────────

export const sendMessage = createAsyncThunk(
  "telnyxMassSms/sendMessage",
  async (data, { rejectWithValue }) => {
    const { token, contact_ids, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_SEND_MESSAGE,
        { ...dataNew, contact_ids: JSON.stringify(contact_ids.map(Number)) },
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data;
      }
      return rejectWithValue({
        message: response?.data?.message || "Failed to send message",
        status: response?.status,
      });
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

export const fetchConversations = createAsyncThunk(
  "telnyxMassSms/fetchConversations",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_GET_CONVERSATIONS,
        dataNew,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data.data || [];
      }
      return [];
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

export const fetchMessages = createAsyncThunk(
  "telnyxMassSms/fetchMessages",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_GET_MESSAGES,
        dataNew,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data.data || [];
      }
      return [];
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

export const replyMessage = createAsyncThunk(
  "telnyxMassSms/replyMessage",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_REPLY_MESSAGE,
        dataNew,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data;
      }
      return rejectWithValue({
        message: response?.data?.message || "Failed to send reply",
        status: response?.status,
      });
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

// ─── Unread Indicators ───────────────────────────────────────

export const markConversationRead = createAsyncThunk(
  "telnyxMassSms/markConversationRead",
  async (data, { rejectWithValue }) => {
    const { token, conversation_id, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_MARK_CONVERSATION_READ,
        { ...dataNew, conversation_id },
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return { conversation_id };
      }
      return rejectWithValue({
        message: response?.data?.message || "Failed to mark as read",
      });
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
      });
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  "telnyxMassSms/fetchUnreadCount",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_GET_UNREAD_COUNT,
        dataNew,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data.data.unread_count;
      }
      return 0;
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
      });
    }
  }
);

// ─── Campaigns / Reports ────────────────────────────────────

export const fetchCampaigns = createAsyncThunk(
  "telnyxMassSms/fetchCampaigns",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_GET_CAMPAIGNS,
        dataNew,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data.data || [];
      }
      return [];
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

export const fetchCampaignDetails = createAsyncThunk(
  "telnyxMassSms/fetchCampaignDetails",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + TELNYX_SMS_GET_CAMPAIGN_DETAILS,
        dataNew,
        makeHeaders(token)
      );
      if (response?.data?.status) {
        return response.data.data;
      }
      return rejectWithValue({
        message: response?.data?.message || "Failed to fetch campaign details",
        status: response?.status,
      });
    } catch (error) {
      return rejectWithValue({
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      });
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────

const initialState = {
  categoriesLoading: false,
  categories: [],
  categoriesError: "",

  contactsLoading: false,
  contacts: [],
  contactsError: "",

  conversationsLoading: false,
  conversations: [],
  conversationsError: "",

  messagesLoading: false,
  messages: [],
  messagesError: "",

  sendLoading: false,
  sendResult: null,
  sendError: "",

  importLoading: false,
  importResult: null,
  importError: "",

  unreadCount: 0,

  campaignsLoading: false,
  campaigns: [],
  campaignsError: "",

  campaignDetailsLoading: false,
  campaignDetails: null,
  campaignDetailsError: "",
};

const telnyxMassSmsSlice = createSlice({
  name: "telnyxMassSms",
  initialState,
  reducers: {
    clearSendResult: (state) => {
      state.sendResult = null;
      state.sendError = "";
    },
    clearImportResult: (state) => {
      state.importResult = null;
      state.importError = "";
    },
    clearCampaignDetails: (state) => {
      state.campaignDetails = null;
      state.campaignDetailsError = "";
    },
    markConversationReadLocal: (state, action) => {
      const conv = state.conversations.find(
        (c) => c.id === action.payload
      );
      if (conv && Number(conv.is_read) === 0) {
        conv.is_read = 1;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
  },
  extraReducers: (builder) => {
    // ─ Categories ─
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = "";
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload?.message || action.error.message;
      })

      .addCase(createCategory.rejected, (state, action) => {
        state.categoriesError = action.payload?.message || action.error.message;
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.categoriesError = action.payload?.message || action.error.message;
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.categoriesError = action.payload?.message || action.error.message;
      })

      // ─ Contacts ─
      .addCase(fetchContacts.pending, (state) => {
        state.contactsLoading = true;
        state.contactsError = "";
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.contactsLoading = false;
        state.contacts = action.payload;
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.contactsLoading = false;
        state.contactsError = action.payload?.message || action.error.message;
      })

      .addCase(addContact.rejected, (state, action) => {
        state.contactsError = action.payload?.message || action.error.message;
      })
      .addCase(updateContact.rejected, (state, action) => {
        state.contactsError = action.payload?.message || action.error.message;
      })
      .addCase(deleteContact.rejected, (state, action) => {
        state.contactsError = action.payload?.message || action.error.message;
      })

      // ─ CSV Import ─
      .addCase(importContactsCsv.pending, (state) => {
        state.importLoading = true;
        state.importError = "";
        state.importResult = null;
      })
      .addCase(importContactsCsv.fulfilled, (state, action) => {
        state.importLoading = false;
        state.importResult = action.payload;
      })
      .addCase(importContactsCsv.rejected, (state, action) => {
        state.importLoading = false;
        state.importError = action.payload?.message || action.error.message;
      })

      // ─ Send Message ─
      .addCase(sendMessage.pending, (state) => {
        state.sendLoading = true;
        state.sendError = "";
        state.sendResult = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sendLoading = false;
        state.sendResult = action.payload;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.sendLoading = false;
        state.sendError = action.payload?.message || action.error.message;
      })

      // ─ Conversations ─
      .addCase(fetchConversations.pending, (state) => {
        state.conversationsLoading = true;
        state.conversationsError = "";
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.conversationsLoading = false;
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.conversationsLoading = false;
        state.conversationsError =
          action.payload?.message || action.error.message;
      })

      // ─ Messages ─
      .addCase(fetchMessages.pending, (state) => {
        state.messagesLoading = true;
        state.messagesError = "";
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        state.messages = action.payload;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.messagesLoading = false;
        state.messagesError = action.payload?.message || action.error.message;
      })

      // ─ Reply ─
      .addCase(replyMessage.rejected, (state, action) => {
        state.messagesError = action.payload?.message || action.error.message;
      })

      // ─ Unread ─
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      .addCase(markConversationRead.fulfilled, (state, action) => {
        const conv = state.conversations.find(
          (c) => c.id === action.payload.conversation_id
        );
        if (conv && !conv.is_read) {
          conv.is_read = 1;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })

      // ─ Campaigns ─
      .addCase(fetchCampaigns.pending, (state) => {
        state.campaignsLoading = true;
        state.campaignsError = "";
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.campaignsLoading = false;
        state.campaigns = action.payload;
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.campaignsLoading = false;
        state.campaignsError = action.payload?.message || action.error.message;
      })

      // ─ Campaign Details ─
      .addCase(fetchCampaignDetails.pending, (state) => {
        state.campaignDetailsLoading = true;
        state.campaignDetailsError = "";
      })
      .addCase(fetchCampaignDetails.fulfilled, (state, action) => {
        state.campaignDetailsLoading = false;
        state.campaignDetails = action.payload;
      })
      .addCase(fetchCampaignDetails.rejected, (state, action) => {
        state.campaignDetailsLoading = false;
        state.campaignDetailsError =
          action.payload?.message || action.error.message;
      });
  },
});

export const { clearSendResult, clearImportResult, clearCampaignDetails, markConversationReadLocal } =
  telnyxMassSmsSlice.actions;
export default telnyxMassSmsSlice.reducer;
